import { useState, useEffect, useCallback } from 'react'
import {
  ProductCategories,
  ProductCategoryLabels,
  type MasterProduct,
  type MasterProductInput,
  type SubstanceOfConcern,
  type MaterialComposition,
  type EndOfLifeData,
  type SparePartsCatalog,
} from '@esf/shared'
import {
  SubstancesOfConcernForm,
  MaterialCompositionForm,
  EndOfLifeForm,
  SparePartsForm,
} from './dpp'
import { getApiBase } from '../utils/api'
import { showDemoToast } from '../utils/demo-toast'

interface DppTemplateFormPageProps {
  templateId?: string | null
  onComplete: () => void
  onCancel: () => void
}

type TabId = 'basic' | 'manufacturer' | 'sustainability' | 'substances' | 'materials' | 'endoflife' | 'notes'

const defaultFormData: MasterProductInput = {
  name: '',
  category: 'seating',
  subcategory: '',
  model: '',
  sku: '',
  gtin: '',
  isActive: true,
  manufacturer: '',
  manufacturerCountry: '',
  materials: [],
  certifications: [],
  carbonFootprint: undefined,
  recyclabilityScore: undefined,
  expectedLifespan: undefined,
  repairabilityScore: undefined,
  description: '',
  hasBattery: false,
  batteryCapacityKwh: undefined,
}

export default function DppTemplateFormPage({ templateId, onComplete, onCancel }: DppTemplateFormPageProps) {
  const base = getApiBase()
  const defaultHeaders = { 'Content-Type': 'application/json' }

  const [formData, setFormData] = useState<MasterProductInput>(defaultFormData)
  const [template, setTemplate] = useState<MasterProduct | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [materialsInput, setMaterialsInput] = useState('')
  const [certificationsInput, setCertificationsInput] = useState('')
  const [activeTab, setActiveTab] = useState<TabId>('basic')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  // DPP-specific state
  const [substancesOfConcern, setSubstancesOfConcern] = useState<SubstanceOfConcern[]>([])
  const [materialComposition, setMaterialComposition] = useState<MaterialComposition | null>(null)
  const [endOfLife, setEndOfLife] = useState<EndOfLifeData | null>(null)
  const [sparePartsCatalog, setSparePartsCatalog] = useState<SparePartsCatalog | null>(null)

  // Check if product has battery (solar_power category)
  const hasBattery = formData.category === 'solar_power'
  const isEditing = !!templateId

  // Load template if editing (demo mode: no-op, templates come from props/state)
  const fetchTemplate = useCallback(async () => {
    if (!templateId) return
    setIsFetching(true)
    // In demo mode, template editing is not available from the API
    setIsFetching(false)
  }, [templateId])

  useEffect(() => {
    if (templateId) {
      fetchTemplate()
    }
  }, [templateId, fetchTemplate])

  // Populate form when template is loaded
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        category: template.category,
        subcategory: template.subcategory || '',
        model: template.model || '',
        sku: template.sku || '',
        gtin: template.gtin || '',
        isActive: template.isActive,
        manufacturer: template.manufacturer || '',
        manufacturerCountry: template.manufacturerCountry || '',
        materials: template.materials || [],
        certifications: template.certifications || [],
        carbonFootprint: template.carbonFootprint,
        recyclabilityScore: template.recyclabilityScore,
        expectedLifespan: template.expectedLifespan,
        repairabilityScore: template.repairabilityScore,
        description: template.description || '',
        hasBattery: template.hasBattery || false,
        batteryCapacityKwh: template.batteryCapacityKwh,
      })
      // Handle materials - could be string[] or MaterialComposition[]
      const materialsArray = template.materials || []
      const materialsStrings = materialsArray.map((m: string | { name: string }) =>
        typeof m === 'string' ? m : m.name
      )
      setMaterialsInput(materialsStrings.join(', '))
      setCertificationsInput((template.certifications || []).join(', '))

      // Load DPP data if available
      if (template.substancesOfConcern) setSubstancesOfConcern(template.substancesOfConcern)
      if (template.materialComposition) setMaterialComposition(template.materialComposition)
      if (template.endOfLifeData) setEndOfLife(template.endOfLifeData)
      if (template.sparePartsCatalog) setSparePartsCatalog(template.sparePartsCatalog)
    }
  }, [template])

  const handleChange = (field: keyof MasterProductInput, value: string | number | boolean | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleArrayChange = (
    field: 'materials' | 'certifications',
    value: string,
    setter: (v: string) => void
  ) => {
    setter(value)
    const arr = value.split(',').map(s => s.trim()).filter(Boolean)
    setFormData(prev => ({ ...prev, [field]: arr }))
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.category) newErrors.category = 'Category is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const submitData: MasterProductInput = {
      ...formData,
      substancesOfConcern: substancesOfConcern.length > 0 ? substancesOfConcern : undefined,
      materialComposition: materialComposition || undefined,
      endOfLifeData: endOfLife || undefined,
      sparePartsCatalog: sparePartsCatalog || undefined,
    }

    showDemoToast('In the live platform, this would save the DPP template.')
    onComplete()
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'manufacturer', label: 'Manufacturer' },
    { id: 'sustainability', label: 'Sustainability' },
    { id: 'substances', label: 'Substances' },
    { id: 'materials', label: 'Material Composition' },
    { id: 'endoflife', label: 'End of Life' },
    { id: 'notes', label: 'Description' },
  ]

  const inputClass = (field: string) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 ${
      errors[field] ? 'border-red-500' : 'border-gray-300'
    }`

  if (isFetching) {
    return (
      <div className="container-page">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </div>
        </div>
      </div>
    )
  }

  if (errors.fetch) {
    return (
      <div className="container-page">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-red-600">{errors.fetch}</p>
          <button onClick={onCancel} className="btn btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page">
      {/* Page Header */}
      <div className="mb-6">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to DPP Templates
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit DPP Template' : 'Create DPP Template'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isEditing
            ? 'Update the product template details and DPP data'
            : 'Create a new product template with pre-populated Digital Product Passport data'}
        </p>
      </div>

      {/* Error Banner */}
      {errors.submit && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-esf-navy text-esf-navy'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => handleChange('name', e.target.value)}
                    className={inputClass('name')}
                    placeholder="e.g., Premium Park Bench Series A"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={e => handleChange('category', e.target.value)}
                    className={inputClass('category')}
                  >
                    {ProductCategories.map(cat => (
                      <option key={cat} value={cat}>
                        {ProductCategoryLabels[cat]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={e => handleChange('subcategory', e.target.value)}
                    className={inputClass('subcategory')}
                    placeholder="e.g., Standard Bench"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={e => handleChange('model', e.target.value)}
                    className={inputClass('model')}
                    placeholder="e.g., PB-2024-A"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={e => handleChange('sku', e.target.value)}
                    className={inputClass('sku')}
                    placeholder="Internal product code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GTIN</label>
                  <input
                    type="text"
                    value={formData.gtin}
                    onChange={e => handleChange('gtin', e.target.value)}
                    className={inputClass('gtin')}
                    placeholder="Global Trade Item Number"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={e => handleChange('isActive', e.target.checked)}
                    className="h-4 w-4 text-esf-navy focus:ring-esf-navy border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                    Active (can be used for new assets)
                  </label>
                </div>
              </div>
            )}

            {/* Manufacturer Tab */}
            {activeTab === 'manufacturer' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={e => handleChange('manufacturer', e.target.value)}
                    className={inputClass('manufacturer')}
                    placeholder="e.g., Environmental Street Furniture Ltd"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin</label>
                  <input
                    type="text"
                    value={formData.manufacturerCountry}
                    onChange={e => handleChange('manufacturerCountry', e.target.value)}
                    className={inputClass('manufacturerCountry')}
                    placeholder="e.g., United Kingdom"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Materials</label>
                  <input
                    type="text"
                    value={materialsInput}
                    onChange={e => handleArrayChange('materials', e.target.value, setMaterialsInput)}
                    className={inputClass('materials')}
                    placeholder="Comma-separated list: Steel, Recycled Plastic, FSC Wood"
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter materials separated by commas</p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certifications</label>
                  <input
                    type="text"
                    value={certificationsInput}
                    onChange={e => handleArrayChange('certifications', e.target.value, setCertificationsInput)}
                    className={inputClass('certifications')}
                    placeholder="Comma-separated list: ISO 14001, FSC, CE Mark"
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter certifications separated by commas</p>
                </div>
              </div>
            )}

            {/* Sustainability Tab */}
            {activeTab === 'sustainability' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Footprint (kg CO2e)</label>
                  <input
                    type="number"
                    value={formData.carbonFootprint ?? ''}
                    onChange={e => handleChange('carbonFootprint', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className={inputClass('carbonFootprint')}
                    placeholder="e.g., 150"
                    min="0"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recyclability Score (0-100)</label>
                  <input
                    type="number"
                    value={formData.recyclabilityScore ?? ''}
                    onChange={e => handleChange('recyclabilityScore', e.target.value ? parseInt(e.target.value) : undefined)}
                    className={inputClass('recyclabilityScore')}
                    placeholder="e.g., 85"
                    min="0"
                    max="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Lifespan (years)</label>
                  <input
                    type="number"
                    value={formData.expectedLifespan ?? ''}
                    onChange={e => handleChange('expectedLifespan', e.target.value ? parseInt(e.target.value) : undefined)}
                    className={inputClass('expectedLifespan')}
                    placeholder="e.g., 25"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repairability Score (0-10)</label>
                  <input
                    type="number"
                    value={formData.repairabilityScore ?? ''}
                    onChange={e => handleChange('repairabilityScore', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className={inputClass('repairabilityScore')}
                    placeholder="e.g., 8.5"
                    min="0"
                    max="10"
                    step="0.1"
                  />
                  <p className="mt-1 text-xs text-gray-500">EU Repairability Index (0-10 scale)</p>
                </div>

                {hasBattery && (
                  <>
                    <div className="lg:col-span-4 pt-4 border-t">
                      <h4 className="font-medium text-gray-900 mb-4">Battery Information</h4>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="hasBattery"
                        checked={formData.hasBattery}
                        onChange={e => handleChange('hasBattery', e.target.checked)}
                        className="h-4 w-4 text-esf-navy focus:ring-esf-navy border-gray-300 rounded"
                      />
                      <label htmlFor="hasBattery" className="text-sm font-medium text-gray-700">
                        Contains Battery
                      </label>
                    </div>
                    {formData.hasBattery && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Battery Capacity (kWh)</label>
                        <input
                          type="number"
                          value={formData.batteryCapacityKwh ?? ''}
                          onChange={e => handleChange('batteryCapacityKwh', e.target.value ? parseFloat(e.target.value) : undefined)}
                          className={inputClass('batteryCapacityKwh')}
                          placeholder="e.g., 2.5"
                          min="0"
                          step="0.1"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Substances Tab */}
            {activeTab === 'substances' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-800 mb-1">ESPR Article 7 Compliance</h4>
                  <p className="text-sm text-blue-700">
                    Document any substances of concern (SVHC, hazardous substances) as required by the EU Ecodesign for Sustainable Products Regulation.
                  </p>
                </div>
                <SubstancesOfConcernForm
                  substances={substancesOfConcern}
                  onChange={setSubstancesOfConcern}
                />
              </div>
            )}

            {/* Material Composition Tab */}
            {activeTab === 'materials' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-800 mb-1">Material Composition</h4>
                  <p className="text-sm text-blue-700">
                    Provide detailed material composition data including recycled content percentages for DPP compliance.
                  </p>
                </div>
                <MaterialCompositionForm
                  composition={materialComposition}
                  onChange={setMaterialComposition}
                />
              </div>
            )}

            {/* End of Life Tab */}
            {activeTab === 'endoflife' && (
              <div className="space-y-8">
                <div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-blue-800 mb-1">End of Life Instructions</h4>
                    <p className="text-sm text-blue-700">
                      Provide disassembly and recycling instructions as required by ESPR for product circularity.
                    </p>
                  </div>
                  <EndOfLifeForm endOfLife={endOfLife} onChange={setEndOfLife} />
                </div>

                <div className="border-t pt-8">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-green-800 mb-1">Spare Parts Catalog (ESPR Article 11)</h4>
                    <p className="text-sm text-green-700">
                      List available spare parts for repair and maintenance to extend product lifespan.
                    </p>
                  </div>
                  <SparePartsForm catalog={sparePartsCatalog} onChange={setSparePartsCatalog} />
                </div>
              </div>
            )}

            {/* Notes/Description Tab */}
            {activeTab === 'notes' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => handleChange('description', e.target.value)}
                  rows={10}
                  className={inputClass('description')}
                  placeholder="Detailed description of this product template..."
                />
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : isEditing ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
